import React, { useEffect, useState } from 'react';
import { Play, FileText, BarChart3, ArrowRight } from 'lucide-react';
import { LineChart, Line, ResponsiveContainer } from 'recharts';
import { Avatar, AvatarImage } from './ui/avatar';
import { Button } from './ui/button';
import { useNavigate } from 'react-router-dom';

const sampleData = [
  { value: 20 },
  { value: 40 },
  { value: 30 },
  { value: 70 },
  { value: 50 },
  { value: 90 },
  { value: 60 },
  { value: 80 },
  { value: 40 },
  { value: 60 },
  { value: 50 }
];

const partnerLogos = [
  { id: 1, name: 'Kammarkollegiet', src: '/lovable-uploads/dc252e25-0141-4625-9fd5-6b9ba6c3517f.png', width: 90 },
  { id: 2, name: 'Upphandlingsmyndigheten', src: '/lovable-uploads/dc252e25-0141-4625-9fd5-6b9ba6c3517f.png', width: 110 },
  { id: 3, name: 'SKR', src: '/lovable-uploads/dc252e25-0141-4625-9fd5-6b9ba6c3517f.png', width: 120 },
  { id: 4, name: 'Konkurrensverket', src: '/lovable-uploads/dc252e25-0141-4625-9fd5-6b9ba6c3517f.png', width: 120 },
  { id: 5, name: 'Sveriges Kommuner', src: '/lovable-uploads/dc252e25-0141-4625-9fd5-6b9ba6c3517f.png', width: 130 },
  { id: 6, name: 'Trafikverket', src: '/lovable-uploads/dc252e25-0141-4625-9fd5-6b9ba6c3517f.png', width: 100 }
];

const Hero = () => {
  const [isVisible, setIsVisible] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 300);
    return () => clearTimeout(timer);
  }, []);

  const handleNavigateToAuth = () => {
    navigate('/auth');
  };

  return (
    <section className="pt-28 md:pt-32 pb-12 md:pb-20 bg-white overflow-hidden relative">
      <div className="absolute inset-0 bg-gradient-to-b from-white/80 via-white to-white/90 z-0"></div>
      
      <div className="container mx-auto px-4 sm:px-6 relative z-10">
        <div className="text-center max-w-4xl mx-auto mb-10 md:mb-14">
          <div className="flex items-center justify-center gap-2 mb-6 sm:mb-8">
            <div className="bg-yellow-300/20 rounded-full px-3 sm:px-4 py-1.5 flex items-center">
              <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1.5 sm:mr-2" viewBox="0 0 24 24" fill="none">
                <path d="M12 4L15 7H9L12 4Z" fill="#FFD100" />
                <path d="M15 17L12 20L9 17H15Z" fill="#FFD100" />
                <path d="M7 9V15L4 12L7 9Z" fill="#FFD100" />
                <path d="M17 15V9L20 12L17 15Z" fill="#FFD100" />
                <rect x="9" y="9" width="6" height="6" fill="#FFD100" />
              </svg>
              <span className="text-xs sm:text-sm font-montserrat font-bold text-darkgray">Sveriges ledande upphandlingsanalys</span>
            </div>
          </div>
          
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-montserrat font-bold text-darkgray mb-4 sm:mb-6">
            Analysera och vinn <br />offentliga upphandlingar
          </h1>
          
          <p className="text-base md:text-lg lg:text-xl text-darkgray/80 mb-8 sm:mb-10 max-w-2xl mx-auto px-2 font-opensans">
            Få djupgående insikter, jämförelser med tidigare upphandlingar 
            och strategiska rekommendationer för att optimera dina anbud.
          </p>
          
          <div className="flex flex-row justify-center items-center gap-3 mb-10 sm:mb-12">
            <Button className="flex items-center justify-center gap-1.5 flex-shrink-0 bg-white text-accent font-montserrat font-bold px-4 py-2 sm:px-5 sm:py-2.5 md:px-6 md:py-3 rounded-full shadow-md hover:shadow-lg transition-all text-sm sm:text-base">
              <Play size={16} className="fill-accent" />
              Se hur det fungerar
            </Button>
            <Button 
              className="bg-yellow-500 text-darkgray font-montserrat font-bold px-4 py-2 sm:px-5 sm:py-2.5 md:px-6 md:py-3 rounded-full shadow-md hover:shadow-lg transition-all text-sm sm:text-base flex-shrink-0 hover:bg-gradient-to-r hover:from-yellow-500 hover:to-yellow-300"
              onClick={handleNavigateToAuth}
            >
              Kom igång gratis
            </Button>
          </div>
          
          <div className="flex items-center justify-center rounded-lg py-2 sm:py-3 px-3 sm:px-4 bg-transparent">
            <div className="flex -space-x-2 sm:-space-x-3 mr-3 sm:mr-4">
              <Avatar className="border-2 border-white w-8 h-8 sm:w-10 sm:h-10">
                <AvatarImage src="https://randomuser.me/api/portraits/men/32.jpg" alt="User" />
              </Avatar>
              <Avatar className="border-2 border-white w-8 h-8 sm:w-10 sm:h-10">
                <AvatarImage src="https://randomuser.me/api/portraits/women/44.jpg" alt="User" />
              </Avatar>
              <Avatar className="border-2 border-white w-8 h-8 sm:w-10 sm:h-10">
                <AvatarImage src="https://randomuser.me/api/portraits/women/68.jpg" alt="User" />
              </Avatar>
              <Avatar className="border-2 border-white w-8 h-8 sm:w-10 sm:h-10">
                <AvatarImage src="https://randomuser.me/api/portraits/men/75.jpg" alt="User" />
              </Avatar>
            </div>
            <div className="flex flex-col items-start">
              <div className="flex items-center text-yellow-500 text-sm sm:text-base">
                ★★★★★
              </div>
              <p className="text-xs sm:text-sm text-darkgray/70 font-opensans">Över 500+ nöjda anbudsgivare</p>
            </div>
          </div>
        </div>
        
        <div className="w-full max-w-5xl mx-auto mt-8 sm:mt-10">
          <div className="bg-white rounded-xl shadow-2xl overflow-hidden border border-gray-200">
            <div className="border-b border-gray-100 p-3 sm:p-4 flex justify-between items-center">
              <div className="flex items-center">
                <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-yellow-500 flex items-center justify-center mr-2 sm:mr-3">
                  <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 4L15 7H9L12 4Z" fill="#333333" />
                    <path d="M15 17L12 20L9 17H15Z" fill="#333333" />
                    <path d="M7 9V15L4 12L7 9Z" fill="#333333" />
                    <path d="M17 15V9L20 12L17 15Z" fill="#333333" />
                    <rect x="9" y="9" width="6" height="6" fill="#333333" />
                  </svg>
                </div>
                <span className="font-montserrat font-bold text-xs sm:text-sm text-darkgray">SNABBAUPPHANDLINGAR</span>
              </div>
              
              <div className="text-xs bg-gray-100 text-gray-500 px-2 sm:px-3 py-1 rounded-md font-opensans">
                Analysdata: Halmstad skolbygge
              </div>
            </div>
            
            <div className="p-4 md:p-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6 md:mb-8">
                <div className="bg-gray-50 p-3 md:p-4 rounded-lg">
                  <h4 className="text-xs md:text-sm text-gray-500 mb-1 font-opensans">Budget</h4>
                  <div className="flex justify-between items-end">
                    <p className="text-xl md:text-2xl font-bold text-darkgray font-montserrat">85 Mkr</p>
                    <div className="text-xs text-orange-500 flex items-center font-opensans">
                      <FileText className="h-3 w-3 mr-1" /> Info
                    </div>
                  </div>
                  <div className="h-12 md:h-16 mt-2">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={sampleData}>
                        <Line type="monotone" dataKey="value" stroke="#FFD100" strokeWidth={2} dot={false} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
                
                <div className="bg-gray-50 p-3 md:p-4 rounded-lg">
                  <h4 className="text-xs md:text-sm text-gray-500 mb-1 font-opensans">Anbudstid</h4>
                  <div className="flex justify-between items-end">
                    <p className="text-xl md:text-2xl font-bold text-darkgray font-montserrat">45 dagar</p>
                    <div className="text-xs text-green-500 flex items-center font-opensans">
                      +5 dagar ↑
                    </div>
                  </div>
                  <div className="h-12 md:h-16 mt-2">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={sampleData}>
                        <Line type="monotone" dataKey="value" stroke="#FFD100" strokeWidth={2} dot={false} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
                
                <div className="bg-gray-50 p-3 md:p-4 rounded-lg">
                  <h4 className="text-xs md:text-sm text-gray-500 mb-1 font-opensans">Kravkomplexitet</h4>
                  <div className="flex justify-between items-end">
                    <p className="text-xl md:text-2xl font-bold text-darkgray font-montserrat">Medel</p>
                    <div className="text-xs text-orange-500 flex items-center font-opensans">
                      <BarChart3 className="h-3 w-3 mr-1" /> Data
                    </div>
                  </div>
                  <div className="h-12 md:h-16 mt-2">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={sampleData}>
                        <Line type="monotone" dataKey="value" stroke="#FFD100" strokeWidth={2} dot={false} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
                
                <div className="bg-gray-50 p-3 md:p-4 rounded-lg">
                  <h4 className="text-xs md:text-sm text-gray-500 mb-1 font-opensans">Konkurrens</h4>
                  <div className="flex justify-between items-end">
                    <p className="text-xl md:text-2xl font-bold text-darkgray font-montserrat">Hög</p>
                    <div className="text-xs text-red-500 flex items-center font-opensans">
                      Trend ↑
                    </div>
                  </div>
                  <div className="h-12 md:h-16 mt-2">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={sampleData}>
                        <Line type="monotone" dataKey="value" stroke="#FFD100" strokeWidth={2} dot={false} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                <div className="bg-gray-50 p-3 md:p-4 rounded-lg h-40 md:h-48">
                  <h4 className="text-xs md:text-sm text-gray-500 mb-1 font-opensans">Nyckelkrav analys</h4>
                  <ul className="text-xs md:text-sm text-darkgray pl-4 list-disc mt-2 font-opensans">
                    <li>Miljöcertifiering: LEED Gold eller motsvarande</li>
                    <li>Energieffektivitet: 30% under BBR-krav</li>
                    <li>Tillgänglighet: Utökade krav över boverkets regler</li>
                    <li>Akustik: Ljudklass B för undervisningslokaler</li>
                  </ul>
                </div>
                
                <div className="bg-gray-50 p-3 md:p-4 rounded-lg h-40 md:h-48">
                  <h4 className="text-xs md:text-sm text-gray-500 mb-1 font-opensans">Rekommendationer</h4>
                  <ul className="text-xs md:text-sm text-darkgray pl-4 list-disc mt-2 font-opensans">
                    <li>Framhäv tidigare erfarenhet av skolbyggnation</li>
                    <li>Detaljera miljö- och energilösningar</li>
                    <li>Inkludera specialister inom akustik i teamet</li>
                    <li>Lägg särskild vikt vid tillgänglighetsanpassningar</li>
                  </ul>
                </div>
              </div>
            </div>
            
            <div className="border-t border-gray-100 p-3 sm:p-4 flex justify-end">
              <Button variant="outline" size="sm" className="text-sm flex items-center gap-1 text-darkgray">
                Se fullständig analys <ArrowRight className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
